import React, {
  forwardRef,
  useState,
  useEffect,
  useRef,
  useImperativeHandle
} from "react";
import PropTypes from "kf-proptypes/widgets";

import { useUserStore } from "base/stores";

import {
  SingleSelectDropdown,
  SingleSelectDropdownItem
} from "../dropdown/singleselect.jsx";
import {
  DROPDOWN_SIZES,
  DROPDOWN_POSITION,
  DROPDOWN_TYPE
} from "../dropdown/constants.js";
import { UserIcon, AVATAR_SIZE } from "@widgets/datadisplay/usericon/index.jsx";
import { UserMenuItem } from "@widgets/datadisplay/usermenuitem/index.jsx";

import "./userselect.override.css";
import styles from "./userselect.css";

const defaultLabelRenderer = (user, disabled, size) => (
  <div className={styles.item}>
    <UserIcon
      size={AVATAR_SIZE.SIZE20}
      className={styles.itemProfileImage}
      userId={user._id}
      name={user.Name}
      disabled={disabled}
    />
    <div style={{ overflow: "hidden" }}>
      <div className={`marutham-600 ellipsis`}>{user.Name}</div>
    </div>
  </div>
);

export function SingleUserSelectComponent(props, ref) {
  const {
    dataSource,
    defaultValue,
    value,
    position,
    labelRenderer,
    onChange,
    size = DROPDOWN_SIZES.BASE,
    readOnly = false,
    disabled = false,
    onSearch,
    onSearchDebounced,
    className,
    containerClassname,
    valueComparer,
    getContainer,
    onOpen,
    onClose,
    placeholder,
    showClearIcon = true,
    onFocus,
    autoComplete,
    isOpen,
    type = DROPDOWN_TYPE.NORMAL,
    checkDataSourceForValue = true,
    showGroup = false,
    hint,
    itemRenderer,
    errors = [],
    id = "singleUserSelect",
    ...restProps
  } = props;

  const [currentDatasource, setCurrentDatasource] = useState([]);
  const selectRef = useRef(null);

  useEffect(function updateDataSource() {
    if (!dataSource) {
      useUserStore
        .getState()
        .getUsers("", 1, showGroup, false, false, 50, id)
        .then((users) => {
          setCurrentDatasource(users);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const defaultSearch = ({ search }) => {
    useUserStore
      .getState()
      .getUsers(search, 1, showGroup)
      .then((users) => {
        setCurrentDatasource(users);
      });
  };

  useImperativeHandle(ref, () => {
    return {
      focus: () => {
        selectRef.current.focus();
      },
      blur: () => {
        selectRef.current.blur();
      }
    };
  });

  return (
    <SingleSelectDropdown
      dataSource={dataSource || currentDatasource}
      defaultValue={defaultValue}
      value={value}
      onOpen={onOpen}
      onClose={onClose}
      onFocus={onFocus}
      size={size}
      showClearIcon={showClearIcon}
      getContainer={getContainer}
      position={position}
      labelRenderer={(user, disabled) => {
        return labelRenderer
          ? labelRenderer(user, disabled, size)
          : defaultLabelRenderer(user, disabled, size);
      }}
      onChange={onChange}
      readOnly={readOnly}
      disabled={disabled}
      dropdownClassName="singleUserSelect"
      containerClassname={`${styles.singleUserSelect} ${containerClassname}`}
      className={`singleUserInput ${className}`}
      onSearch={onSearch}
      placeholder={placeholder}
      onSearchDebounced={onSearchDebounced || defaultSearch}
      valueComparer={valueComparer}
      isOpen={isOpen}
      ref={selectRef}
      type={type}
      autoComplete={autoComplete}
      itemRenderer={(user, index) => {
        return itemRenderer ? (
          itemRenderer(user, index)
        ) : (
          <SingleSelectDropdownItem label={user.Name} value={user._id}>
            <UserMenuItem
              userId={user._id}
              name={user.Name}
              email={user.Email}
              kind={user.Kind}
            />
          </SingleSelectDropdownItem>
        );
      }}
      checkDataSourceForValue={checkDataSourceForValue}
      errors={errors}
      hint={hint}
      {...restProps}
    />
  );
}

SingleUserSelectComponent.propTypes = {
  dataSource: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      Kind: PropTypes.string,
      Email: PropTypes.string,
      FirstName: PropTypes.string,
      Name: PropTypes.string,
      Groups: PropTypes.arrayOf(
        PropTypes.shape({
          _id: PropTypes.string
        })
      )
    })
  ),
  defaultValue: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  position: PropTypes.string,
  labelRenderer: PropTypes.func,
  onChange: PropTypes.func,
  showClearIcon: PropTypes.bool,
  readOnly: PropTypes.bool,
  className: PropTypes.string,
  containerClassname: PropTypes.string,
  onSearch: PropTypes.func,
  onSearchDebounced: PropTypes.func,
  onOpen: PropTypes.func,
  onFocus: PropTypes.func,
  type: PropTypes.string,
  size: PropTypes.string,
  placeholder: PropTypes.string,
  valueComparer: PropTypes.func,
  getContainer: PropTypes.func,
  onClose: PropTypes.func,
  isOpen: PropTypes.bool,
  disabled: PropTypes.bool,
  hint: PropTypes.string,
  autoComplete: PropTypes.string,
  id: PropTypes.string,
  errors: PropTypes.arrayOf(PropTypes.string),
  showGroup: PropTypes.bool,
  checkDataSourceForValue: PropTypes.bool,
  itemRenderer: PropTypes.func
};

export const SingleUserSelect = forwardRef(SingleUserSelectComponent);

export { DROPDOWN_POSITION, DROPDOWN_TYPE, DROPDOWN_SIZES };
